import { DocumentFormat, DocumentFormatLabels } from "../constants";

// 1. Επέκταση FormField για υποστήριξη multi-select / checkboxes
export interface FormField<T> {
  name: keyof T;
  label: string;
  type: "text" | "number" | "date" | "select" | "multiselect" | "file";
  required?: boolean;
  options?: { label: string; value: string | number }[];
}

// 2. DTO του Χειρουργείου
export interface PatientSurgeryDto {
  surgeryId: number;
  patientId: number;
  surgeryTypeId?: number;
  surgeryDescription?: string;
  surgeryDate?: string;
}

// 3. DTO Επιστροφής (Response)
export interface PatientDocumentDto {
  documentId: number;
  patientId: number;
  documentTypeId: number;
  fileName: string;
  filePath: string;
  active: boolean;
  documentDescr: string;
  documentRemarks?: string;
  examinationDate?: string;
  medicalCenter?: string;
  format: number;
  patientSurgery?: PatientSurgeryDto[];
}

export interface DocumentViewDto {
  patientId: number;
  documentTypeId: number;
  file: File;
  filePath: string;
  format: number;
  fileExtension?: string;
}

export type DocumentViewFormData = Omit<
  DocumentViewDto,
  "filePath" | "fileExtension"
>;

export const patientdocumentKeyId = "documentId" as const;

// 4. DTO Δημιουργίας (Request)
export interface PatientDocumentCreateDto {
  patientId: number;
  documentTypeId: number;
  fileName: string;
  file: File;
  documentRemarks?: string;
  examinationDate?: string;
  medicalCenter?: string;
  format: number;
  patientSurgeryIds?: number[];
}

export type PatientDocumentCreateFormData = Omit<
  PatientDocumentCreateDto,
  "patientId"
>;

// 5. Ομογενοποιημένα keys της φόρμας
export const patientDocumentFormData: (keyof PatientDocumentCreateFormData)[] =
  [
    "documentTypeId",
    "fileName",
    "format",
    "file",
    "examinationDate",
    "medicalCenter",
    "patientSurgeryIds",
    "documentRemarks",
  ];

// --- HELPER FUNCTIONS ΓΙΑ MAPPING ---

const getFormatOptions = () =>
  Object.values(DocumentFormat)
    .filter((val) => typeof val === "number" && val !== DocumentFormat.NONE)
    .map((formatVal) => ({
      label:
        DocumentFormatLabels[formatVal as keyof typeof DocumentFormatLabels] ??
        `Format ${formatVal}`,
      value: formatVal,
    }));

const getDocumentTypeOptions = (documentTypes: Array<any> = []) =>
  documentTypes.map((dt: any) => {
    // Αν είναι ήδη mapped { label, value }
    if (dt && typeof dt === "object" && "label" in dt && "value" in dt) {
      return { label: dt.label, value: dt.value };
    }

    // Αν είναι raw object από το backend
    return {
      label: dt.documentDescr ?? dt.DocumentDescr ?? "Χωρίς περιγραφή",
      value: dt.documentTypeId ?? dt.DocumentTypeId,
    };
  });

const getSurgeryOptions = (surgeries: PatientSurgeryDto[] = []) =>
  surgeries.map((s) => ({
    label: `${s.surgeryDescription ?? "Χειρουργείο"} (${
      s.surgeryDate ? new Date(s.surgeryDate).toLocaleDateString("el-GR") : "-"
    })`,
    value: s.surgeryId,
  }));

// --- FORM FIELDS CONFIGURATIONS ---

export const documentViewFields = (
  documentTypes: Array<{ documentTypeId: number; documentDescr: string }> = [],
): FormField<DocumentViewFormData>[] => [
  {
    name: "documentTypeId",
    label: "Τύπος Εγγράφου",
    type: "select",
    required: true,
    options: getDocumentTypeOptions(documentTypes),
  },
  {
    name: "format",
    label: "Τύπος Αρχείου (Format)",
    type: "select",
    required: true,
    options: getFormatOptions(),
  },
  {
    name: "file",
    label: "Αρχείο",
    type: "file",
    required: true,
  },
];

export const patientDocumentCoreFields = (
  documentTypes: Array<{ documentTypeId: number; documentDescr: string }> = [],
): FormField<PatientDocumentCreateFormData>[] => [
  {
    name: "documentTypeId",
    label: "Τύπος Εγγράφου",
    type: "select",
    required: true,
    options: getDocumentTypeOptions(documentTypes), // ✅ Καθαρό mapping από raw documentTypes
  },
  {
    name: "fileName",
    label: "Τίτλος Εξέτασης",
    type: "text",
    required: true,
  },
  {
    name: "format",
    label: "Τύπος Αρχείου (Format)",
    type: "select",
    required: true,
    options: getFormatOptions(),
  },
  {
    name: "file",
    label: "Αρχείο",
    type: "file",
    required: true,
  },
];

export const patientDocumentExtraFields = (
  surgeries: PatientSurgeryDto[] = [],
): FormField<PatientDocumentCreateFormData>[] => [
  {
    name: "examinationDate",
    label: "Ημερομηνία Εξέτασης",
    type: "date",
    required: false,
  },
  {
    name: "medicalCenter",
    label: "Ιατρικό Κέντρο",
    type: "text",
    required: false,
  },
  {
    name: "patientSurgeryIds",
    label: "Σχετιζόμενα Χειρουργεία",
    type: "multiselect",
    required: false,
    options: getSurgeryOptions(surgeries),
  },
  {
    name: "documentRemarks",
    label: "Παρατηρήσεις Εγγράφου",
    type: "text",
    required: false,
  },
];

export const patientDocumentCreateFields = (
  documentTypes: Array<{ documentTypeId: number; documentDescr: string }> = [],
  surgeries: PatientSurgeryDto[] = [],
): FormField<PatientDocumentCreateFormData>[] => [
  ...patientDocumentCoreFields(documentTypes), // ✅ Περνάμε κατευθείαν τα raw documentTypes
  ...patientDocumentExtraFields(surgeries),
];
